import { autoinject, Container, customAttribute, View, ViewEngine } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ActivableViewModel } from 'framework/aurelia_tools/activable-view-model';
import { ViewModelReference } from 'framework/aurelia_tools/common-types';
import tippy, { Instance, Props } from 'tippy.js';

@autoinject
@customAttribute('af-popover')
export class PopoverCustomAttribute {
  @bindable({ primaryProperty: true }) view = '';
  @bindable model: unknown = {};
  @bindable viewModel: ViewModelReference = ActivableViewModel;
  @bindable options: PopoverOptions = null;
  @bindable enabled = true;
  @bindable templatePath: string = null;

  private containerView: View;
  private containerElement: HTMLDivElement;
  private tippy: Instance<Props>;

  constructor(private element: Element, private container: Container, private viewEngine: ViewEngine) { }

  bind() {
    // intentionally empty, do not remove
    // prevents initial "...Changed()" calls
  }

  attached() {
    if (this.enabled)
      this.create();
  }

  detached() {
    this.destroy();
  }

  private create() {
    this.viewEngine.loadViewFactory(this.templatePath ? this.templatePath : DEFAULT_TEMPLATE).then(factory => {
      const childContainer = this.container.createChild();
      this.containerView = factory.create(childContainer);

      this.containerElement = document.createElement('div');
      this.containerElement.classList.add(CLASS_COMPONENT);

      this.containerView.bind(this); 
      this.containerView.appendNodesTo(this.containerElement);

      this.element.parentNode.insertBefore(this.containerElement, this.element.nextSibling);
      this.tippy = tippy(this.element, { ...DEFAULT_OPTIONS, ...this.options, allowHTML: true, content: this.containerElement });
    });
  }

  private destroy() {
    if (this.tippy) (this.tippy.destroy(), this.tippy = null);
    if (this.containerElement) (this.containerElement.remove(), this.containerElement = null);
    if (this.containerView) (this.containerView.unbind(), this.containerView = null);
  }

  optionsChanged() {
    if (this.tippy != null)
      this.tippy.setProps(this.options);
  }

  enabledChanged() {
    this.enabled
      ? this.containerView == null && this.create()
      : this.tippy != null && this.destroy();
  }
}

export type PopoverOptions = Omit<Partial<Props>, 'allowHTML' | 'content'>;

const DEFAULT_TEMPLATE = 'framework/html_behaviors/af-popover.html';
const CLASS_COMPONENT = 'af-popover';

const DEFAULT_OPTIONS: PopoverOptions = {
  trigger: 'mouseenter focus',
  triggerTarget: null,
  placement: 'auto',
  arrow: true,
  interactive: true,
  zIndex: 9999,
  maxWidth: 350,
  theme: 'light'
};
